<script lang="ts">
    /**
     * This component is based off of the lcd-metadata component from media-js components:
     * https://github.pie.apple.com/amp-web/components/blob/d10ef951e0fb608f0df910d7c853b9a9e7078f59/src/components/lcd-metadata/lcd-metadata.tsx
     *
     * NOTE: This is a basic implementation to allow displaying LCD metadata in a specific way for Classical.  It lacks a few features that
     *       will need to be implemented separately, as the intention is for Onyx to supply all of the data needed to render the component,
     *       including updated metadata for the currently playing track, handling links, handling timed metadata, etc.
     *
     * TODO: rdar://139446102 (Onyx Web: Update shared PlayerLCD component for Music and Podcasts)
     *
     */
    import type {
        Realm,
        TimedMetadata,
    } from '@amp/web-app-components/src/components/PlayerLCD/types';
    import type { ClassicalArtist, LCDTextFragment } from './types';
    import MarqueeText from '@amp/web-app-components/src/components/MarqueeText/MarqueeText.svelte';

    /**
     * The now playing item.
     */
    export let nowPlayingItem: any | null = null;

    /**
     * Timed metadata (i.e. streaming Radio stations)
     */
    export let timedMetadata: TimedMetadata | null = null;

    /**
     * App realm.  Currently music, podcasts, or classical
     */
    export let realm: Realm;

    /**
     * Config to use to display the contextual menu.
     */
    export let contextualMenuConfig: any | null = null;

    /**
     * Badge, in this case, refers to the Favorites button, which is conditional
     * on the user being signed in with a Premium subscription. This flag just signals
     * that the element needs to render.
     */
    export let hasBadge: boolean = false;

    /**
     * Checks if Preview or Radio Badge exists on secondary marquee line
     * the Preview badge is rendered when a user is NOT signed in with a
     * Premium subscription. The Radio badge exists in the context of the Music app
     * and renders if the track is related to a radio station
     */
    export let hasSecondaryBadge: boolean = false;

    /**
     * translate function provided by the parent app.
     */
    export let translateFn: (
        str: string,
        values?: Record<string, string | number>,
    ) => string;

    $: artistName =
        timedMetadata?.performer ||
        nowPlayingItem?.attributes?.artistName ||
        nowPlayingItem?.artistName;
    // Singular reference to render artists to the LCD
    $: artists = nowPlayingItem?.artists || [{ text: artistName }];

    $: albumName =
        timedMetadata?.album ||
        nowPlayingItem?.attributes?.albumName ||
        nowPlayingItem?.albumName;
    $: composerName =
        nowPlayingItem?.attributes?.composerName ||
        nowPlayingItem?.composerName;
    $: movementName =
        nowPlayingItem?.attributes?.movementName ||
        nowPlayingItem?.movementName;
    $: workName =
        nowPlayingItem?.attributes?.workName || nowPlayingItem?.workName;
    $: title = timedMetadata?.title || nowPlayingItem?.title || '';
    $: eyebrowText = nowPlayingItem?.attributes?.eyebrowText;

    $: primaryLineContent = (() => {
        if (typeof window === 'undefined') {
            return;
        }

        if (!nowPlayingItem) {
            return [];
        }

        if (realm === 'classical') {
            if (composerName && workName) {
                // Building the colon separator into composer name string
                // otherwise it will have a space in front of
                return [
                    {
                        text: `${composerName}\u003A`,
                        isInert: !nowPlayingItem?.composerAction,
                        action: nowPlayingItem?.composerAction,
                    } as LCDTextFragment,
                    {
                        text: workName,
                        isInert: !nowPlayingItem?.workAction,
                        action: nowPlayingItem?.workAction,
                    } as LCDTextFragment,
                ];
            }
        }

        return [{ text: title } as LCDTextFragment].concat(
            nowPlayingItem?.isExplicitItem
                ? [
                      {
                          icon: 'e.square.fill',
                          text: translateFn('MediaComponents.Explicit'),
                          isInert: true,
                      },
                  ]
                : [],
        );
    })();

    $: secondaryLineContent = (() => {
        if (typeof window === 'undefined') {
            return;
        }
        const separator = { text: ' \u2014 ', isInert: true }; // em-dash

        if (!nowPlayingItem) {
            return [];
        }

        if (realm === 'classical') {
            if (movementName && artists?.length) {
                const artistElements = [
                    {
                        text: movementName,
                        isInert: true,
                    } as LCDTextFragment,
                    separator,
                ];

                artists.forEach((artist: ClassicalArtist, idx: number) => {
                    artistElements.push({
                        text: artist.subtitle || artist.text,
                        action: artist.segue,
                        isInert: !artist.segue,
                        separator:
                            idx < artists.length - 1
                                ? translateFn(
                                      'MediaComponents.Common.ListDelimiter', // comma
                                  )
                                : undefined,
                    } as LCDTextFragment);
                });

                return artistElements;
            }
        }

        let content = [];

        if (artistName) {
            content.push({ text: artistName });
        }

        if (nowPlayingItem?.type === 'podcast-episodes' && eyebrowText) {
            if (content.length > 0) {
                content.push(separator);
            }
            content.push({ text: eyebrowText });
            return content;
        }

        if (albumName) {
            // When album content or album name is defined, add a separator only when artist content is also defined
            if (content.length > 0) {
                content.push(separator);
            }

            content.push({ text: albumName });
        }

        if (nowPlayingItem?.isLiveRadioStation) {
            const editorailNotes = nowPlayingItem.attributes?.editorialNotes;
            const tagline = editorailNotes?.short ?? editorailNotes?.tagline;

            if (content.length > 0) {
                content.push(separator, { text: nowPlayingItem.title });
            } else {
                content.push({ text: tagline ?? nowPlayingItem.title });
            }
        }

        return content;
    })();
</script>

{#if nowPlayingItem}
    <div class="lcd-meta" aria-live="assertive" data-testid="lcd-metadata">
        {#if primaryLineContent}
            <div class="lcd-meta__primary-container">
                <MarqueeText
                    marqueeStyle="primary"
                    content={primaryLineContent}
                    {contextualMenuConfig}
                    enableMarqueeControls={realm === 'classical'}
                    on:navigate
                >
                    <slot name="context-menu" slot="context-menu" />
                </MarqueeText>

                {#if hasBadge}
                    <span
                        class="lcd-meta__badge-placeholder"
                        data-testid="lcd-metadata-badge-placeholder"
                    />
                {/if}
            </div>
        {/if}
        {#if secondaryLineContent}
            <MarqueeText
                marqueeStyle="secondary"
                content={secondaryLineContent}
                enableMarqueeControls={realm === 'classical'}
                {hasSecondaryBadge}
                on:navigate
            />
        {/if}
    </div>
{/if}

<style lang="scss">
    @use '@amp/web-shared-styles/app/core/globalvars' as *;

    .lcd-meta {
        max-width: 100%;
        height: calc(var(--lcd-height, 44px) - 3px);
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        align-items: var(--lcd-justify-text, center);
        justify-content: center;
        overflow: hidden;
        position: relative;

        :global(.lcd-meta) {
            max-width: 100%;
        }
    }

    .lcd-meta__primary-container {
        width: 100%;
        max-width: 100%;

        :global(.lcd-meta) {
            min-width: 0;
        }
    }

    .lcd-meta__icon {
        width: 16px;
        height: 16px;
        color: var(--lcd-meta-icon-color, var(--systemSecondary));
    }

    .lcd-meta__icon--menu {
        color: var(--keyColor, var(--musicKeyColor, var(--systemBlue)));

        .lcd-meta.lcd__podcasts & {
            color: var(--keyColor, var(--podcastKeyColor, var(--systemBlue)));
        }
    }

    .lcd-meta__badge-placeholder {
        width: var(--lcd-meta-badge-placeholder-width);
        display: block;
        flex-shrink: 0;

        @supports #{'selector(.lcd-meta__primary:has(.not-marquee))'} {
            .lcd-meta__primary:has(.not-marquee) ~ & {
                width: 0;
            }
        }
    }
</style>
